/**
 * A Source Template's parameters, rendered from its declaration.
 *
 * **Nothing in this file names a publisher.** That is the whole test of the
 * model: PFF asks for a season, a division, a weeks filter and thirteen
 * reports; ESPN asks for a league, a season, a season type and four categories;
 * and both arrive here as the same three kinds — single choice, multiple choice,
 * and a free number. A publisher's codes stay on the server, so `REGPO` and
 * `seasontype=3` are never something a person has to know.
 *
 * The hint is a declared field rather than prose in a screen, because the things
 * worth saying are facts about a publisher: that ESPN's categories select
 * *players* rather than columns, or that PFF's All-Star filter returns thirty
 * bowl-game participants and succeeds quietly. Writing those here would make
 * this component know a publisher, and the next one would arrive with prose of
 * its own to add.
 *
 * A checkbox list rather than a multi-select for the multiple-choice kind: PFF
 * ships thirteen reports all selected by default, and a native multi-select
 * shows a scrolling box in which "all of them" and "the last one you clicked"
 * look nearly identical.
 *
 * **Which parameters are shown is read, not decided.** A declaration may say
 * that a parameter is only asked for some choices, and that an option implies
 * values for others — ESPN is one menu entry over two endpoint surfaces, so
 * picking MLS puts away the American football categories and moves the season
 * onto soccer's calendar. That is still the declaration doing the work: the
 * rule arrives with the parameter and this file applies it without knowing what
 * a league is.
 *
 * **And which *options* are on offer is read the same way.** An option can
 * declare the choices it cannot be picked under, and a row ruled out by what is
 * already chosen is **not listed** — `offeredOptions`. That is
 * `add-source-usability/04`, where Wyscout's conference menu answered one
 * question — *is this conference published for the gender I chose* — in three
 * places at three times in three vocabularies, the worst of them red text under
 * the whole group **after** the compose button.
 *
 * Listing those rows disabled-with-a-reason was the first fix and it was still
 * the publisher's internal shape on screen: under *Men*, six conferences
 * Wyscout publishes for women only sat there apologising, where the honest
 * answer is that the menu has twenty-four entries. The menu now shows what can
 * be picked.
 *
 * **Nothing here says what a publisher currently holds.** A second rule briefly
 * put *"it would fetch no players"* under an option measured empty, and two of
 * the four conferences it named had filled up by the time anybody read it. A
 * population is a measurement with a date on it; a menu is read as present
 * tense. Which Feeds came back with nobody in them is reported after a fetch,
 * on the Source screen, where it is a reading rather than a memory.
 *
 * One boundary, stated rather than left to be found: a **single** choice is
 * marked by nothing here. No template declares a rule on one yet, the server
 * refuses with the declared sentence if an API caller sends one, and both
 * `offeredOptions` and `withAvailableChoices` deliberately leave a chosen value
 * standing — blanking a required control is this frontend's most-repeated
 * defect.
 */

import type {
  ParameterisedTemplate,
  SourceTemplate,
  TemplateParameter,
  TemplateValue,
  TemplateValues,
} from "../api";
import {
  applicableParameters,
  impliedValue,
  offeredOptions,
  optionsFor,
  parameterApplies,
  startingValue,
  withAvailableChoices,
} from "../api";
import { Checkbox, Field, FieldSet, Muted, Select, TextInput } from "./ui";

export function TemplateParameters({
  template,
  values,
  onChange,
  /** The parameter the server refused, if any, so its control can say so. */
  invalid,
  message,
  sport = "",
}: {
  template: ParameterisedTemplate;
  values: TemplateValues;
  onChange: (values: TemplateValues) => void;
  invalid?: string | null;
  message?: string | null;
  /**
   * The sport the Source menu was filtered to, or "" where there is no filter —
   * which is every caller but Add Source.
   *
   * It narrows the choices a parameter offers, and nothing else: the rule and
   * the marking are both `optionsFor`'s, so this file still names no publisher
   * and does not know that a league decides a sport. A filter cannot hide a
   * choice declaring no sport and cannot hide the chosen one, so the worst it
   * can do is mark a row.
   */
  sport?: string;
}) {
  /**
   * One value changed, and everything that change decides.
   *
   * A single choice can settle more than itself: it can imply values for other
   * parameters, and it can bring a parameter into the form that was not being
   * asked a moment ago. Both are read off the declaration — this function names
   * no publisher and no parameter, and would do the same thing for the next
   * source that spans two endpoint surfaces.
   *
   * An implication **overrules what is in the box**, rather than filling only an
   * empty one. Switching the choice switches which of a publisher's surfaces
   * answers, and last surface's season left standing is the failure the
   * implication exists to prevent — a profile composed a year behind with every
   * check green. It is one visible control, and it can be typed over again.
   */
  const set = (key: string, value: TemplateValue) => {
    const changed = template.parameters.find((parameter) => parameter.key === key);
    let next: TemplateValues = { ...values, [key]: value };
    if (changed?.kind === "single_choice") {
      // **The implications of the choice that just changed, and no others.**
      // Re-applying every parameter's implications would reach values nobody
      // touched: changing ESPN's season type would put a hand-typed 2024 back
      // to the league's implied 2025, with nothing on screen to explain it —
      // and the server, which treats an implication as a fallback for a value
      // it was not sent, would have honoured the 2024.
      const option = changed.options.find((o) => o.value === value);
      next = { ...next, ...(option?.implies ?? {}) };
      // A parameter that has just come into the form has never been answered,
      // so it starts where its declaration says — or where the choices so far
      // imply, which is the order the server resolves a default in. Only the
      // missing ones: a choice switched away from and back again keeps what was
      // filled in.
      for (const parameter of template.parameters) {
        if (!parameterApplies(parameter, next) || next[parameter.key] !== undefined) continue;
        const implied = impliedValue(template, parameter.key, next);
        // Through `startingValue`, the same way the form's opening values are
        // resolved: a parameter arriving mid-form starts on a choice the sport
        // filter allows, or the two paths would answer "what does this control
        // start on" by different rules. Unreachable with today's declarations —
        // ESPN's league is the only parameter declaring a sport and it is
        // always asked — and that is exactly why it is worth not leaving to the
        // next two-surface publisher to discover.
        next = {
          ...next,
          [parameter.key]: startingValue(
            parameter,
            implied === undefined ? parameter.default : implied,
            sport,
          ),
        };
      }
      // **Last, and after the implications.** A single choice can take a later
      // parameter's options off the menu — Wyscout's Gender does, and six of
      // its conferences are published for women only — so a row that was
      // ticked a moment ago can be a row that cannot be composed now. Dropping
      // it here is what makes the refusal unreachable rather than merely late:
      // before this, a men's profile with Big Sky ticked got red text under the
      // whole group after the compose button (`add-source-usability/04`). The
      // rule is the declaration's, and `withAvailableChoices` names no
      // publisher and no parameter.
      next = withAvailableChoices(template, next);
    }
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-md">
      {applicableParameters(template, values).map((parameter) => (
        <Parameter
          key={parameter.key}
          parameter={parameter}
          value={values[parameter.key]}
          // The whole set, not only this parameter's own value: an option can
          // declare that an *earlier* choice takes it off the menu, and the
          // row has to be able to say so before anybody ticks it.
          values={values}
          error={invalid === parameter.key ? message ?? "That value was not accepted." : null}
          sport={sport}
          onChange={(value) => set(parameter.key, value)}
        />
      ))}
    </div>
  );
}

function Parameter({
  parameter,
  value,
  values,
  error,
  sport,
  onChange,
}: {
  parameter: TemplateParameter;
  value: TemplateValue | undefined;
  /** Everything chosen so far, for the options that depend on an earlier one. */
  values: TemplateValues;
  error: string | null;
  sport: string;
  onChange: (value: TemplateValue) => void;
}) {
  if (parameter.kind === "number") {
    return (
      <Field label={parameter.label} hint={parameter.hint ?? undefined} error={error}>
        <TextInput
          type="number"
          value={typeof value === "number" || typeof value === "string" ? value : ""}
          min={parameter.minimum ?? undefined}
          max={parameter.maximum ?? undefined}
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      </Field>
    );
  }

  if (parameter.kind === "single_choice") {
    const chosen = typeof value === "string" ? value : "";
    const option = parameter.options.find((o) => o.value === chosen);
    return (
      <Field
        label={parameter.label}
        // The chosen option's own hint wins over the parameter's, because it is
        // the more specific thing to say: the parameter explains the choice,
        // the option explains *this* choice. PFF's All-Star filter is the case
        // — the caveat belongs to the one option that carries it.
        hint={option?.hint ?? parameter.hint ?? undefined}
        error={error}
      >
        <Select
          value={chosen}
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange(e.target.value)}
        >
          {optionsFor(parameter, sport, chosen).map((row) => (
            <option key={row.option.value} value={row.option.value}>
              {row.option.label}
              {/* Present only because it is the one chosen, and the filter
                  excludes it. Marked rather than left to be inferred, exactly
                  as the Source menu marks a publisher kept for the same reason:
                  an unmarked MLS in a football-filtered list reads as football.
                  See `optionsFor`. */}
              {row.offFilter && ` (not ${sport})`}
            </option>
          ))}
        </Select>
      </Field>
    );
  }

  const selected = Array.isArray(value) ? value : [];
  const toggle = (option: string) =>
    onChange(
      selected.includes(option)
        ? selected.filter((item) => item !== option)
        : // The template's own order, not the order they were ticked: Feed
          // positions are a property of the publisher.
          parameter.options.map((o) => o.value).filter((o) => o === option || selected.includes(o)),
    );

  return (
    <FieldSet label={parameter.label} hint={parameter.hint ?? undefined}>
      <div className="grid grid-cols-1 gap-xxs fold:grid-cols-2">
        {offeredOptions(parameter, values).map((choice) => (
          <label key={choice.value} className="flex items-start gap-xs py-xxs">
            <Checkbox
              className="mt-[2px]"
              checked={selected.includes(choice.value)}
              onChange={() => toggle(choice.value)}
            />
            <span className="text-body-sm text-ink">
              {choice.label}
              {/* The option's own hint, which is a fact about the thing rather
                  than about an earlier choice or about this season — that this
                  entry is not a conference at all, or is one conference under
                  two of the publisher's names. */}
              {choice.hint && (
                <span className="block text-caption text-muted-soft">{choice.hint}</span>
              )}
            </span>
          </label>
        ))}
      </div>
      {/* A group cannot use `Field`'s error slot — it is a `<label>`, and one
          cannot speak for thirteen checkboxes — so the complaint sits under the
          group it refers to. */}
      {error && <Muted tone="text-danger-ink">{error}</Muted>}
    </FieldSet>
  );
}

/** One line naming a composed source, for a read-only view of a profile. */
export function describeTemplate(
  template: SourceTemplate | undefined,
  values: TemplateValues,
): string {
  if (!template) return "";
  // Only what this profile was actually asked. A publisher spanning two endpoint
  // surfaces declares questions belonging to each, and naming the ones its other
  // surface asks would describe a soccer league by the football categories
  // nobody chose.
  return applicableParameters(template, values)
    .map((parameter) => {
      const value = values[parameter.key];
      if (Array.isArray(value)) {
        // The count rather than the names: thirteen report names is a paragraph,
        // and "13 of 13" is the thing somebody is checking.
        return `${parameter.label}: ${value.length} of ${parameter.options.length}`;
      }
      const option = parameter.options.find((o) => o.value === String(value));
      return `${parameter.label}: ${option?.label ?? String(value ?? "—")}`;
    })
    .join(" · ");
}
